import * as React from "react";
import * as globals from "../../../globals";
import * as modelsGet from "../../../models/get";
import * as modelsPost from "../../../models/post";
import * as modelsForm from "../../../models/form";
import * as utils from "../../../utils/utils";
import * as apiAccounts from "../../../api/accounts";
import * as apiSettings from "../../../api/settings";
import { Window } from "../../../components/window";
import { Loader, LoaderStyle } from "../../../components/loader";
import { Button, ButtonType } from "../../../components/button";
import { Alert, AlertType } from "../../../components/alert";
import { ImageCropper } from "../../../components/imageCropper";

interface IProperties
{
    /**
     * Wird aufgerufen sobald sich das Fenster schließt.
     */
    onClose?: (refresh?: boolean) => void
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Enthält den geöffneten Datensatz.
     */
    user?: modelsGet.IUser,

    /**
     * Gibt an ob der "Speichern" Button eingeschaltet ist.
     */
    isSaveButtonEnabled?: boolean,

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export class ImageDialog extends React.Component<IProperties, IState>
{
    private _imageCropper: ImageCropper;

    /**
     * Erstellt eine neue Instanz der Klasse @see UserProfileImageDialog.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        this.state = {
            isLoading: true,
            errors: {}
        };

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        var user: modelsGet.IUser = await apiAccounts.getCurrentUser();

        this.setState((state: IState) =>
        {
            state.isLoading = false;
            state.errors = {};
            state.alertMessage = null;
            state.alertType = null;
            state.user = user;

            return state;
        });
    }

    /**
     * Wird ausgeführt, bevor der Dialog sich schließt.
     */
    private onClosing = (refresh: boolean = false): boolean =>
    {
        const { onClose } = this.props;
        onClose && onClose(refresh);
        return true;
    }

    /**
     * Ändert den geöffneten Datensatz oder erstellt einen neuen.
     */
    private onSaveClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            var image: string = this._imageCropper.crop();
            await apiAccounts.changePicture(image);
            await globals.getCurrentUserAsync(true);

            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Rendert das Formular/den Content Bereich.
     */
    private renderContent(): JSX.Element
    {
        const {
            user,
            errors,
            alertMessage,
            alertType
        } = this.state;

        return (
            <div className="settings_dialog_content">
                <ImageCropper
                    ref={(ref) => this._imageCropper = ref}
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0
                    }}
                    aspectRatio={4 / 3}
                    croppedImageHeight={200}
                    croppedImageWidth={200}
                    onSrcChanged={() => this.setState({ isSaveButtonEnabled: true })} />
            </div>
        );
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const {
            isLoading,
            user,
            isSaveButtonEnabled,
            errors,
            alertMessage,
            alertType
        } = this.state;

        var icon: string = "loading";
        var title: string = "Daten werden geladen...";
        var buttonHeader: string = "";

        if (!isLoading)
        {
            icon = "pencil";
            title = "Profilbild ändern";
            buttonHeader = "Speichern";
        }

        return (
            <Window
                isOpen={true}
                titleIcon={icon}
                title={title}
                onClosing={this.onClosing}
                isMaximized={false}
                isResizable={false}
                width={600}
                height={560}
                onEnterKeyUp={this.onSaveClick}
                onEscapeKeyUp={this.onClosing}>
                <div className="settings_dialog">
                    <Alert
                        style={utils.getGridChildStyle(1, 1, 1, 1)}
                        alertType={alertType}
                        isVisible={alertMessage ? true : false}
                        isClosable={true}
                        onHide={() => this.setState({ alertMessage: null, alertType: null })}>
                        {alertMessage}
                    </Alert>
                    {isLoading ? <Loader loaderStyle={LoaderStyle.style1} /> : this.renderContent()}
                    <div className="settings_dialog_actions">
                        <div style={{ flexGrow: 1 }}></div>
                        <Button
                            text={buttonHeader}
                            type={ButtonType.primary}
                            isDisabled={!isSaveButtonEnabled}
                            onClick={this.onSaveClick} />
                        <Button
                            text="Abbrechen"
                            type={ButtonType.default}
                            onClick={() => this.onClosing(false)} />
                    </div>
                </div>
            </Window>
        );
    }
}